export const domain =
  process.env.GATSBY_ACTIVE_ENV === "staging"
    ? "https://staging-cms.endlazer.com"
    : "http://localhost:1337"

export const getStrapiUrl = (url) => {
  if (!url) return url
  if (url.startsWith("http://") || url.startsWith("https://")) return url
  if (
    process.env.GATSBY_ACTIVE_ENV !== "staging" &&
    url.startsWith("/uploads/")
  )
    return `https://staging-cms.endlazer.com${url}`
  return `${domain}${url}`
}

export const isExternalLink = (value = "") => {
  if (!value || typeof value !== "string") return false
  return (
    /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(value) ||
    value.startsWith("mailto:") ||
    value.startsWith("tel:")
  )
}

export const hasInvalidPathSegment = (value = "") => {
  if (!value || typeof value !== "string") return true
  return /(^|\/)(undefined|null)(\/|$)/i.test(value)
}

export const buildLocalizedPath = (language, path) => {
  if (path == null) return ""
  const raw = String(path).trim()
  if (!raw) return ""
  if (hasInvalidPathSegment(raw)) return ""
  const looksLikeBareDomain =
    /^[a-z0-9.-]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(raw) && !raw.startsWith("/")
  if (looksLikeBareDomain) {
    const protocol = raw.startsWith("localhost") ? "http://" : "https://"
    return `${protocol}${raw}`
  }
  if (isExternalLink(raw) || raw.startsWith("#")) return raw

  const normalizedLanguage = (language || "").trim().replace(/^\/+|\/+$/g, "")
  const withSingleLeadingSlash = `/${raw}`.replace(/\/{2,}/g, "/")

  if (!normalizedLanguage) {
    return withSingleLeadingSlash
  }

  if (/^\/(de|en)(\/|$)/i.test(withSingleLeadingSlash)) {
    return withSingleLeadingSlash
  }

  return `/${normalizedLanguage}${withSingleLeadingSlash}`.replace(
    /\/{2,}/g,
    "/"
  )
}

export const isAnchorNavPage = function () {
  if (typeof window !== "undefined") {
    // converting  url string  to an array .
    // getting the last item or string  from the array
    const pageUrl = window.location.pathname

    const match =
      /\/services\/(.+)/.exec(pageUrl) ||
      /\/blog\/(.+)/.exec(pageUrl) ||
      /\/case-studies\/(.+)/.exec(pageUrl)

    if ((match && match[1].length > 0) || pageUrl.includes("ueber-uns/")) {
      return true
    } else {
      return false
    }
  }
}

export const formatRichText = (richText) => {
  return typeof richText === "string"
    ? richText.replace(/\b\n\b/g, "<br/>")
    : richText?.map(({ richText }) => richText.replace(/\b\n\b/g, "<br/>"))
}

export const isMobile = () => {
  return typeof window === "undefined" || window.innerWidth < 768 // returns true if device is mobile
}

export function isDesktop() {
  return typeof window === "undefined" || window.innerWidth > 1024 // returns true if device is Desktop
}

export function debounce(method, delay) {
  clearTimeout(method._tId)
  method._tId = setTimeout(function () {
    method()
  }, delay)
}

export function isServicesLandingPages() {
  if (typeof window !== "undefined") {
    // converting  url string  to an array .
    // getting the last item or string  from the array
    const pageUrl = window.location.pathname
    const regex = /\/services\/(.+)/ // Regex pattern

    const match = regex.exec(pageUrl)

    if (match && match[1].length > 0) {
      return true
    } else {
      return false
    }
  }
}

export function getSinglePage(webPages, allSitePage) {
  const webPagesKeys = webPages && Object.keys(webPages)
  const selectedWebPageKey =
    webPagesKeys && webPagesKeys?.find((webPage) => webPages[webPage])
  if (selectedWebPageKey) {
    const selectedWebPage = webPages[selectedWebPageKey]
    const selectedLanguage = selectedWebPage?.language
    if (!selectedLanguage || hasInvalidPathSegment(selectedLanguage)) {
      return ""
    }
    const seletedPageSlug = allSitePage.find(
      (page) =>
        selectedWebPage?.slug &&
        page.path.includes(selectedWebPage.slug) &&
        page.path.startsWith(`/${selectedWebPage.language}`)
    )

    if (seletedPageSlug) {
      return seletedPageSlug.path
    } else {
      switch (selectedWebPageKey) {
        case "about_us":
          // code block
          return selectedLanguage === "de" ? "/de/ueber-uns/" : "/en/ueber-uns/"
        case "careers_overview":
          return selectedLanguage === "de" ? "/de/careers/" : "/en/careers/"
        case "services_overview":
          return selectedLanguage === "de" ? "/de/services/" : "/en/services/"
        case "cases_overview":
          return selectedLanguage === "de"
            ? "/de/case-studies/"
            : "/en/case-studies/"
        case "blogs_overview":
          return selectedLanguage === "de" ? "/de/blog/" : "/en/blog/"
        case "contact":
          return selectedLanguage === "de" ? "/de/kontakt/" : "/en/contact/"
        case "plainpage":
          if (
            !selectedWebPage?.slug ||
            hasInvalidPathSegment(selectedWebPage.slug)
          )
            return ""
          return `/${selectedLanguage}/${selectedWebPage.slug}`
        default:
          return ""
      }
    }
  } else {
    return undefined
  }
}

export function stripString(str) {
  const newstr = str && str.trim().replace(/\s+/g, "-").toLowerCase()
  return `${newstr}`
}

export const elementIsVisibleInViewport = (el, partiallyVisible = false) => {
  const { top, left, bottom, right } = el.getBoundingClientRect()
  const { innerHeight, innerWidth } = window
  return partiallyVisible
    ? ((top > 0 && top < innerHeight) ||
        (bottom > 0 && bottom < innerHeight)) &&
        ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
}

export const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId)
  if (section) {
    section.scrollIntoView({ behavior: "smooth" })
  }
}

export const scrollToTop = (behavior = "smooth") => {
  if (typeof window === "undefined") return

  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const resolvedBehavior = prefersReducedMotion ? "auto" : behavior
  const targets = [
    window,
    document.scrollingElement,
    document.body,
    document.documentElement,
  ]

  targets.forEach((target) => {
    if (!target) return

    if (typeof target.scrollTo === "function") {
      target.scrollTo({
        top: 0,
        left: 0,
        behavior: resolvedBehavior,
      })
      return
    }

    target.scrollTop = 0
    target.scrollLeft = 0
  })
}

export const getRandomIndex = (testimonials) => {
  if (testimonials && testimonials.length > 0) {
    let randomIndex = Math.floor(Math.random() * testimonials.length)
    return randomIndex
  }
}

export function truncateText(text, maxLength) {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "..."
  }
  return text
}

export function hasPreviousTruthyElements(array, currentIndex) {
  for (let i = 0; i < currentIndex; i++) {
    if (array[i]) {
      return true // Found a truthy value
    }
  }
  return false // No truthy values found
}

export function extractredYear(date) {
  if (!date) return ""
  const splittedDate = date.split("-")
  const year = splittedDate[0].length === 2 ? splittedDate[2] : splittedDate[0]

  const currentYear = year.slice(2)
  return currentYear
}

export const normalizePath = (path) => path.replace(/^\/|\/$/g, "") // Removes leading/trailing slashes

const flattenAttributeValue = (value) => {
  if (!value) return value
  if (Array.isArray(value)) {
    return value.map((item) => flattenAttributeValue(item))
  }
  if (Array.isArray(value?.data)) {
    return value.data.map((item) => flattenEntity(item)).filter(Boolean)
  }
  if (value?.data) {
    return flattenEntity(value.data)
  }
  if (typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = flattenAttributeValue(value[key])
      return acc
    }, {})
  }
  return value
}

const flattenEntity = (entity) => {
  if (!entity) return entity
  const { id, attributes } = entity
  if (!attributes || typeof attributes !== "object") {
    return entity
  }
  const flattened = { ...(id ? { id } : {}) }
  Object.entries(attributes).forEach(([key, value]) => {
    flattened[key] = flattenAttributeValue(value)
  })
  return flattened
}

export const flattenStrapiResponse = (response = {}) => {
  if (!response) return {}

  if ("data" in response) {
    if (Array.isArray(response.data)) {
      return response.data.map((item) => flattenEntity(item)).filter(Boolean)
    }
    return flattenEntity(response.data)
  }

  return Object.keys(response).reduce((acc, key) => {
    const value = response[key]
    if (value == null) {
      acc[key] = value
      return acc
    }

    if (Array.isArray(value?.data)) {
      acc[key] = value.data.map((item) => flattenEntity(item)).filter(Boolean)
      return acc
    }

    if (value?.data) {
      acc[key] = flattenEntity(value.data)
      return acc
    }

    acc[key] = flattenEntity(value)
    return acc
  }, {})
}
